import { Directive, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { DateTimeHelper } from '../../util/date-time-helper';
import { SubSink } from 'subsink';
import { TimeCoordinateMappingService } from '../../time-mapping/time-coordinate-mapping.service';
import { CalendarService } from '../../services/calendar.service';
import { filter } from 'rxjs/operators';
import { CronJobsService } from '../../../../core/services/cron-jobs.service';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';
import { calendarCronjobsInterval } from '../../calendar.config';

@Directive({
  selector: '[appNowIndicator]',
  standalone: false,
})
export class NowIndicatorDirective implements OnInit, OnDestroy {
  private errorMsg = '';
  private cronJobUUID: string;
  private readonly subs = new SubSink();

  private readonly renderer = inject(ShiveRenderService);
  private readonly el = inject(ElementRef);
  private readonly timeCoordinateMapperService = inject(TimeCoordinateMappingService);
  private readonly calendarService = inject(CalendarService);
  private readonly calendarCronJobsService = inject(CronJobsService);

  ngOnInit(): void {
    this.initNowIndicator();

    const updateNowIndicator = () => {
      const now = DateTimeHelper.setSecondsAndMillisToZero(new Date());
      this.updateIndicatorPos(this.el, now);
    };

    this.cronJobUUID = this.calendarCronJobsService.registerJob(
      updateNowIndicator,
      calendarCronjobsInterval,
    );

    updateNowIndicator();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.calendarCronJobsService.deregisterJob(this.cronJobUUID);
  }

  private updateIndicatorPos(el: ElementRef<HTMLElement>, now: Date): void {
    this.timeCoordinateMapperService.pixelsPerMinuteCalculated$
      .pipe(filter((ppm) => !!ppm))
      .subscribe(() => {
        const pixelOffset = this.timeCoordinateMapperService.mapDurationToScreenLength(
          DateTimeHelper.startOfToday(),
          now,
        );
        this.renderer.setStyle(el.nativeElement, 'top', `${pixelOffset}px`);
      });
  }

  private initNowIndicator(): void {
    this.subs.sink = this.calendarService.calendarViewInitialized$.subscribe({
      next: (initialized) => {
        if (initialized) {
          const now = DateTimeHelper.setSecondsAndMillisToZero(new Date());
          this.updateIndicatorPos(this.el, now);
        }
      },
      error: (err) => {
        this.errorMsg = err;
      },
    });
  }
}
